import {
  joinDisplayNameOf,
  newJoinId,
  nextJoinDisplayName,
  pruneJoinConfigs,
} from "../utils/joinUtils";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import Button from "components/common/Button";
import { JoinEditor } from "components/reportsV3/components/JoinEditor";
import * as labels from "config.labels";
import React, { useCallback, useMemo, useState } from "react";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const EMPTY_JOIN = {
  joinName: "",
  joinDisplayName: "",
  leftTable: "",
  rightTable: "",
  joinType: "LEFT",
  columns: [],
};

const JoinsPanel = ({
  selectedSourcesArray,
  sourceDisplayNameMap,
  sourceColumnQueries,
  joinConfigs,
  onJoinConfigsChange,
}) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const getColumnsForSource = useCallback(
    (tableName) => {
      const idx = selectedSourcesArray.indexOf(tableName);
      if (idx < 0) return [];
      const query = sourceColumnQueries[idx];
      return query?.data ?? [];
    },
    [selectedSourcesArray, sourceColumnQueries]
  );

  const buildTableOptionsForIndex = useCallback(
    (joinIndex) => {
      const sourceOpts = selectedSourcesArray.map((tbl) => ({
        value: tbl,
        label: sourceDisplayNameMap[tbl] || tbl,
      }));

      const priorJoinOpts = (joinConfigs ?? [])
        .slice(0, joinIndex !== null ? joinIndex : joinConfigs?.length ?? 0)
        .map((cfg) => ({
          value: cfg.joinName,
          label: joinDisplayNameOf(cfg),
        }));

      return [...sourceOpts, ...priorJoinOpts];
    },
    [selectedSourcesArray, sourceDisplayNameMap, joinConfigs]
  );

  const sourceTableSet = useMemo(() => new Set(selectedSourcesArray), [
    selectedSourcesArray,
  ]);

  const getColumnsForTable = useCallback(
    (tableValue) => {
      if (!tableValue) return [];

      if (sourceTableSet.has(tableValue)) {
        return getColumnsForSource(tableValue);
      }

      const cfg = (joinConfigs ?? []).find((j) => j.joinName === tableValue);
      if (!cfg) return [];

      const pairColumns = (cfg.columns ?? []).flatMap((pair) => [
        pair.left,
        pair.right,
      ]);
      const leftCols = getColumnsForTable(cfg.leftTable);
      const rightCols = getColumnsForTable(cfg.rightTable);

      const seen = new Set();
      const result = [];
      for (const col of [...leftCols, ...rightCols]) {
        if (seen.has(col.internalName)) continue;
        seen.add(col.internalName);
        result.push(col);
      }
      for (const name of pairColumns) {
        if (seen.has(name)) continue;
        seen.add(name);
        result.push({
          internalName: name,
          displayName: name,
          dataType: "text",
        });
      }
      return result;
    },
    [sourceTableSet, getColumnsForSource, joinConfigs]
  );

  const handleClickJoin = (index) => {
    setEditingIndex(index);
    setEditorOpen(true);
  };

  const handleClickAdd = () => {
    setEditingIndex(null);
    setEditorOpen(true);
  };

  const handleEditorCancel = () => {
    setEditorOpen(false);
    setEditingIndex(null);
  };

  const handleEditorAccept = (config) => {
    const next = [...(joinConfigs ?? [])];
    const targetIndex =
      editingIndex !== null && editingIndex < next.length
        ? editingIndex
        : next.length;
    const displayName = joinDisplayNameOf(config);
    const nameHeldElsewhere = next.some(
      (cfg, index) =>
        index !== targetIndex && joinDisplayNameOf(cfg) === displayName
    );
    const accepted = {
      ...config,
      // A join keeps the internal name it was created with; only a join that
      // has none - a new one - is given one.
      joinName: config.joinName || newJoinId(),
      joinDisplayName:
        !displayName || nameHeldElsewhere
          ? nextJoinDisplayName(next, targetIndex)
          : displayName,
    };

    if (targetIndex < next.length) {
      next[targetIndex] = accepted;
    } else {
      next.push(accepted);
    }
    onJoinConfigsChange(next);
    setEditorOpen(false);
    setEditingIndex(null);
  };

  const handleRemoveJoin = (index) => {
    const next = [...(joinConfigs ?? [])];
    next.splice(index, 1);
    onJoinConfigsChange(pruneJoinConfigs(next, new Set(selectedSourcesArray)));
  };

  const editingInitial =
    editingIndex !== null && joinConfigs?.[editingIndex]
      ? joinConfigs[editingIndex]
      : {
          ...EMPTY_JOIN,
          joinDisplayName: nextJoinDisplayName(joinConfigs, null),
        };

  const editorTableOptions = useMemo(
    () => buildTableOptionsForIndex(editingIndex),
    [buildTableOptionsForIndex, editingIndex]
  );

  // The display names the editor may not reuse: those held by every join other
  // than the one being edited.
  const otherDisplayNames = useMemo(
    () =>
      (joinConfigs ?? [])
        .filter((_cfg, index) => index !== editingIndex)
        .map(joinDisplayNameOf),
    [joinConfigs, editingIndex]
  );

  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant={"subtitle2"}
          color={"text.secondary"}
          sx={{ fontSize: "1.2rem", fontWeight: 400 }}
        >
          {translateLabels(labels.JOINS_PANEL_JOINS, { default: "Joins" })}
        </Typography>
        <Button startIcon={<AddIcon />} onClick={handleClickAdd}>
          {translateLabels(labels.JOINS_PANEL_ADD_JOIN, {
            default: "ADD JOIN",
          })}
        </Button>
      </Box>

      {(joinConfigs ?? []).length > 0 && (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <List dense disablePadding>
            {(joinConfigs ?? []).map((cfg, index) => (
              <ListItem
                key={index}
                disablePadding
                secondaryAction={
                  <IconButton
                    edge={"end"}
                    size={"small"}
                    aria-label={translateLabels(
                      labels.JOINS_PANEL_REMOVE_JOIN,
                      {
                        default: "Remove join {name}",
                        replacements: { name: joinDisplayNameOf(cfg) },
                      }
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveJoin(index);
                    }}
                  >
                    <CloseIcon fontSize={"small"} />
                  </IconButton>
                }
              >
                <ListItemButton onClick={() => handleClickJoin(index)}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <EditIcon fontSize={"small"} color={"action"} />
                  </ListItemIcon>
                  <ListItemText
                    primary={joinDisplayNameOf(cfg)}
                    secondary={`${cfg.joinType} — ${
                      (cfg.columns ?? []).length
                    } ${translateLabels(labels.JOINS_PANEL_PAIRS, {
                      default: "pair(s)",
                    })}`}
                    slotProps={{
                      primary: { variant: "body2", fontWeight: 600 },
                      secondary: { variant: "caption" },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      <Dialog
        open={editorOpen}
        onClose={handleEditorCancel}
        maxWidth={false}
        slotProps={{ paper: { sx: { width: 700 } } }}
      >
        <DialogContent sx={{ p: 0 }}>
          <JoinEditor
            key={editorOpen ? `${editingIndex}` : "closed"}
            initial={editingInitial}
            tableOptions={editorTableOptions}
            sourceTableNames={sourceTableSet}
            otherDisplayNames={otherDisplayNames}
            getColumnsForTable={getColumnsForTable}
            onAccept={handleEditorAccept}
            onCancel={handleEditorCancel}
          />
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default JoinsPanel;
